import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/store"
import { deckEndpoints } from "@/store/apis/deckAPI"
import { gameplayEndpoints } from "@/store/apis/gameplayAPI"
import {
  GAMEPLAY_CONNECTION_GRACE_MS,
  GameplaySelectors,
  setConnectionOverlay
} from "@/store/slices/gameplay"

const getNavigatorOnline = (): boolean => {
  if (typeof navigator === "undefined") return true
  return navigator.onLine !== false
}

const useIsBrowserOnline = (): boolean => {
  const [isOnline, setIsOnline] = useState(getNavigatorOnline)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  return isOnline
}

const useIsGameplayQueryFailed = (sessionId: string | undefined): boolean =>
  useAppSelector((state) => {
    if (!sessionId) return false
    const stateQuery = gameplayEndpoints.endpoints.getGameplayState.select({
      sessionId
    })(state as never)
    const handQuery = deckEndpoints.endpoints.getDeckHand.select(sessionId)(
      state as never
    )
    return stateQuery.isError === true || handQuery.isError === true
  })

export const useGameConnectionOverlay = () => {
  const dispatch = useAppDispatch()
  const sessionId = useAppSelector(GameplaySelectors.selectSessionId())
  const isSocketConnected = useAppSelector(
    GameplaySelectors.selectSocketConnection()
  )
  const isMatchFinished = useAppSelector(
    GameplaySelectors.selectIsMatchFinished()
  )
  const isOnline = useIsBrowserOnline()
  const isQueryFailed = useIsGameplayQueryFailed(sessionId)

  useEffect(() => {
    if (!sessionId) return

    if (isMatchFinished) {
      dispatch(setConnectionOverlay(false))
      return
    }

    // Immediate (no grace) when browser reports offline OR a key query
    // failed — the user is definitely disconnected, don't pretend we're fine.
    if (!isOnline || isQueryFailed) {
      dispatch(setConnectionOverlay(true))
      return
    }

    if (isSocketConnected) {
      dispatch(setConnectionOverlay(false))
      return
    }

    // Socket not connected (yet/anymore) but browser thinks online —
    // give it a brief grace window before showing overlay.
    const timer = setTimeout(() => {
      dispatch(setConnectionOverlay(true))
    }, GAMEPLAY_CONNECTION_GRACE_MS)

    return () => clearTimeout(timer)
  }, [
    sessionId,
    isMatchFinished,
    isSocketConnected,
    isOnline,
    isQueryFailed,
    dispatch
  ])
}
